import { useEffect, useState, useCallback } from 'react';
import { Truck, PackageCheck, Ban, AlertTriangle, Loader2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { transfersAPI } from '@/services/api';

const fmt = (d) => d ? new Date(d).toLocaleString('en-IN') : '-';

// HQ control: receive a stuck transfer on behalf of the store, or cancel it and return stock to source.
export const InTransitControl = ({ toast, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState(null); // 'receive' | 'cancel'
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await transfersAPI.listInTransit()).data.items || []); } catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (o, m) => { setTarget(o); setMode(m); setNotes(''); setQty(Object.fromEntries(o.items.map(i => [i.product_id, String(i.dispatched_qty)]))); };
  const close = () => { setTarget(null); setMode(null); };

  const submit = async () => {
    if (!notes.trim()) return toast({ title: mode === 'receive' ? 'Enter a receipt note (why HQ is receiving for the store)' : 'Enter a cancel reason', variant: 'destructive' });
    setBusy(true);
    try {
      if (mode === 'receive') {
        const items = target.items.map(i => ({ product_id: i.product_id, received_qty: parseFloat(qty[i.product_id] || 0), damaged_qty: 0 }));
        const r = await transfersAPI.receiveOrder(target.id, { items, receipt_notes: notes });
        const short = r.data.items.filter(i => (i.shortage_qty || 0) > 0);
        toast({ title: `${target.transfer_number} received — stock added to ${target.destination_name}`, description: short.length ? `${short.length} line(s) short, transfer marked "discrepancy"` : 'All quantities received' });
      } else {
        const r = await transfersAPI.cancelInTransit(target.id, notes);
        toast({ title: `${target.transfer_number} cancelled`, description: r.data.restored_to_source.length ? `Returned to source: ${r.data.restored_to_source.map(x => `${x.product_name || x.sku} ×${x.qty}`).join(', ')}` : 'No stock had been deducted, nothing to return' });
      }
      close(); load(); onChanged?.();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  if (!loading && !rows.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-cyan-200 bg-cyan-50/60 p-4" data-testid="in-transit-control">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-cyan-900 flex items-center gap-2"><Truck className="w-5 h-5" /> In transit — not yet received by store ({rows.length})</h2>
        <span className="text-xs text-cyan-800">HQ can receive on the store's behalf (adds store stock) or cancel and return stock to source.</span>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-cyan-700" /> : (
        <div className="grid gap-2 md:grid-cols-2">
          {rows.map(o => (
            <div key={o.id} className="bg-white rounded-lg border p-3 text-sm" data-testid={`in-transit-${o.transfer_number}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{o.transfer_number} <Badge className="ml-1 bg-cyan-100 text-cyan-700 capitalize">{o.status.replace('_', ' ')}</Badge></div>
                  <div className="text-xs text-gray-500">{o.source_name} → <b>{o.destination_name}</b> · dispatched {fmt(o.dispatched_at)}{o.days_in_transit != null ? ` · ${o.days_in_transit} day(s) ago` : ''}</div>
                </div>
                {o.possible_duplicate_of?.length > 0 && <Badge className="bg-amber-100 text-amber-800 shrink-0" data-testid={`dup-${o.transfer_number}`}><Copy className="w-3 h-3 mr-1" /> Looks duplicate of {o.possible_duplicate_of.join(', ')}</Badge>}
              </div>
              <ul className="mt-2 text-xs text-gray-700 space-y-0.5">
                {o.items.map(i => <li key={i.product_id} className="flex justify-between"><span className="truncate mr-2">{i.product_name} <span className="text-gray-400">{i.sku}</span></span><span>×{i.dispatched_qty}{i.source_short_qty > 0 ? <span className="text-amber-700 ml-1" title="Source did not have enough stock when dispatched">(short {i.source_short_qty} at source)</span> : null}</span></li>)}
              </ul>
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => open(o, 'receive')} data-testid={`receive-for-store-${o.transfer_number}`}><PackageCheck className="w-4 h-4 mr-1" /> Receive for store</Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => open(o, 'cancel')} data-testid={`cancel-transit-${o.transfer_number}`}><Ban className="w-4 h-4 mr-1" /> Cancel & return stock</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-lg" data-testid="in-transit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{mode === 'receive' ? <><PackageCheck className="w-5 h-5 text-emerald-600" /> Receive {target?.transfer_number} for {target?.destination_name}</> : <><Ban className="w-5 h-5 text-red-600" /> Cancel {target?.transfer_number}</>}</DialogTitle></DialogHeader>
          {target && mode === 'receive' && (
            <div className="space-y-3 text-sm">
              <p className="text-gray-600">Enter the quantity physically present at the store. Less than dispatched is recorded as a shortage (status "discrepancy"). Stock is added to the store immediately and appears in Store Stock / POS.</p>
              {target.items.map(i => (
                <div key={i.product_id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0"><div className="font-medium truncate">{i.product_name}</div><div className="text-xs text-gray-500">{i.sku} · dispatched {i.dispatched_qty}</div></div>
                  <Input type="number" min="0" step="any" value={qty[i.product_id] ?? ''} onChange={e => setQty(q => ({ ...q, [i.product_id]: e.target.value }))} className="w-24 text-right" data-testid={`recv-qty-${i.sku}`} />
                </div>
              ))}
              <div><Label>Receipt note (required)</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Store confirmed 2 pcs on shelf, 1 missing" data-testid="in-transit-notes" /></div>
            </div>
          )}
          {target && mode === 'cancel' && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> The transfer is marked cancelled and the quantity that was deducted from <b>{target.source_name}</b> is put back there. Nothing is added to the store.</div>
              <div><Label>Reason (required)</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Duplicate of ST-261010-0001" data-testid="in-transit-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}>Back</Button>
            <Button onClick={submit} disabled={busy} className={mode === 'receive' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'} data-testid="in-transit-confirm">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{mode === 'receive' ? 'Receive & add to store stock' : 'Cancel transfer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
